import { useCallback, useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { createStore, del, set, values, type UseStore } from 'idb-keyval';
import { isRetryableMutationRefusal, refusalReason } from './refusal';
import { queryClient } from './query';
import { sameAccount, sameAccountOrNobody, type AccountRef } from './account';
import { keyOf } from './idempotency';
import { onSignalOrVisible } from './browser';
import { isKeptForAccount } from './write-refusals';

/**
 * Offline work the server refused (§4 Wave 1, ticket 029).
 *
 * ## The silence this replaces
 *
 * 016 removed a *lie*: the banner no longer announces *"Everything you did offline has gone
 * through"* when a queued write failed. What it left was **silence**. A
 * `payment.recordManual` that replays into a 409 — or exhausts `retry: 3` — ends
 * `status: 'error'` in the mutation cache, and then:
 *
 *   - **nothing persists it.** `shouldDehydrateMutation` stores only `isPaused` writes, and a
 *     failed write is not paused. Measured: `persisted rows = 0`.
 *   - **nothing keeps it.** The mutation cache garbage-collects it about five minutes later.
 *     Measured: `after gc = 0`.
 *   - **nothing mentions it.** No screen in the app reads that state.
 *
 * Money was collected at a door and the shop has no record of it.
 *
 * 016 deliberately did not just add a banner state, and it was right: *"1 thing couldn't be
 * sent — it's still here"* would have been false, because it was about to be discarded and
 * there was nowhere to retry it from. **So recovery comes first and the message second.**
 * This file is the recovery.
 *
 * ## Why its own store, again
 *
 * Same answer as the photo queue, one layer along: the mutation cache cannot hold this. It
 * persists paused writes only, it evicts on its own schedule, and the persister rewrites the
 * *entire* dehydrated client on every state change. A refused payment must outlive all three.
 *
 * ## What it will not do
 *
 * It never discards a row on its own. Every row leaves because the server took it or because
 * a person said so — a queue that quietly drops work is the defect this file exists to
 * remove, wearing a hat.
 */

/** One offline write the server refused, or that ran out of retries. */
export interface FailedWrite {
  /**
   * The write's own client-minted idempotency key, lifted from its variables — the row's
   * identity.
   *
   * ⚠️ **Not TanStack's `mutationId`**, which is a per-session counter starting at 1: two
   * sessions of the same app collide on it immediately, and it means nothing after a
   * restart. This key is the one the server dedupes on, which makes it the only value that
   * still identifies the same write tomorrow.
   */
  mutationId: string;
  clientId: string;
  /** tRPC procedure path — `payment.recordManual`. */
  path: string;
  /** The write's input, kept verbatim so a retry is the same request, not a new one. */
  variables: Record<string, unknown>;
  /**
   * Who made it: the account stamped on its pending row when it was queued, never
   * whoever happens to be signed in when the refusal lands.
   *
   * Null only on a row an earlier release wrote before anyone was known to be signed in. The
   * one-time move of old writes gives such rows to the device's last account, marked
   * `adopted`, or removes them when no account is known (legacy-writes.ts).
   */
  userId: string | null;
  orgId: string | null;
  failedAt: number;
  /** What the server said, in its own words. */
  reason: string;
  /** Whether asking again could go differently. */
  retryable: boolean;
  /**
   * Whether this row actually reached the disk.
   *
   * False only when the store refused it — a full phone, a private-mode origin, a corrupted
   * database. The row is still held for the session, because losing it outright is this
   * ticket's own defect; but the panel promises *"still on this phone"* to everything it
   * shows, and for this one that promise ends when the app is closed. So it is marked, and
   * the panel says the different thing.
   */
  durable: boolean;
  /**
   * Whether this row was given to an account rather than recorded under one.
   *
   * ⚠️ **That is a guess, and the panel must not state it as a fact.** Only an earlier
   * release's refusal with no account is ever given away (to the device's last account),
   * and on a shop tablet that may be a different technician entirely. Visibility still beats
   * silence, but "A payment you took" would be a false claim, so an adopted row says it was
   * taken *on this phone* instead.
   *
   * A retry of an adopted row runs under whoever is signed in now, so the server attributes
   * it to them. That is a knowing trade against losing the money, and it is why the row says
   * out loud that it may not be yours.
   */
  adopted: boolean;
}

/** A row as the screen sees it. `sending` is memory-only, for the same reason as photos. */
export type FailedWriteView = FailedWrite & { sending: boolean };

/** Durable side. An interface so the logic is testable without IndexedDB. */
export interface FailedWriteStore {
  all(): Promise<FailedWrite[]>;
  put(item: FailedWrite): Promise<void>;
  remove(mutationId: string): Promise<void>;
}

/**
 * What each replayable write is, said the way a tech would say it.
 *
 * A panel that reads `payment.recordManual` on a porch is a panel nobody acts on. Exhaustive
 * over `REPLAYABLE` and nothing else, and a test pins it both ways — a path with no label
 * would render a blank line, and a label for a path that can no longer be queued hides the
 * fact that `REPLAYABLE` shrank.
 */
export const WRITE_LABEL: Readonly<Record<string, string>> = {
  'visit.enRoute': 'Marking yourself on the way',
  // The tech's words for it, not dispatch's: they tapped "Running behind", and the log has
  // to name the thing they remember doing. "Rescheduling a visit" is what the OFFICE calls
  // this write, and a tech reading it in a failed-write list would not recognise their tap.
  'visit.reschedule': 'Pushing a stop back',
  'visit.start': 'Starting a stop',
  // en_route → scheduled only: "I had to turn back" and "Not leaving yet". It never stops work.
  'visit.abort': 'Marking yourself not on the way',
  'visit.complete': 'Finishing a job',
  'costing.startTimer': 'Starting your timer',
  'costing.stopTimer': 'Stopping your timer',
  'note.append': 'A note you wrote',
  'checklist.setItem': 'A checklist tick',
  'payment.recordManual': 'A payment you took',
  'inventory.useOnJob': 'Parts you used on a job',
  'inventory.returnFromJob': 'Parts you put back on the van',
  'inventory.transfer': 'Parts you moved',
  'restocks.check': 'Restock you checked',
  'equipment.create': 'Equipment you added',
  'equipment.update': 'A change you made to equipment',
  'equipment.tag': 'A tag you put on equipment',
  'equipment.untag': 'A tag you took off equipment',
  'equipment.retire': 'Equipment you retired',
  'customField.create': 'A field you added',
  'customField.update': 'A change you made to a field',
  'customField.set': 'Something you filled in',
  'customField.remove': 'A field you removed',
  'attachment.remove': 'A photo you deleted',
  'ticket.flag': 'A problem you flagged',
  'ticket.message': 'A message on a ticket',
};

/** The label, or something in English. Never the procedure path — that is jargon on a porch. */
export function labelFor(path: string): string {
  return WRITE_LABEL[path] ?? 'Something you did';
}

/**
 * A prefix minted once per app start, for the synthetic keys of writes that carry no
 * idempotency key of their own.
 *
 * 🔴 **Without it those keys collide across sessions and one row silently overwrites
 * another.** The only per-write identity available is TanStack's `mutationId`, a counter that
 * restarts at 1 every session — so `local:1` from yesterday and `local:1` from today are the
 * same key in a store that outlives both, and the older refusal is destroyed by the newer
 * one. Unique per session, stable within it, which is exactly as much as can be promised for
 * a write the client never keyed.
 */
const SESSION = Math.random().toString(36).slice(2, 10);

const byFailedAt = (a: FailedWrite, b: FailedWrite) => a.failedAt - b.failedAt;

export class FailedWriteLog {
  readonly #store: FailedWriteStore;
  readonly #send: (path: string, variables: Record<string, unknown>) => Promise<unknown>;
  readonly #onDelivered: () => void;

  readonly #rows = new Map<string, FailedWrite>();
  /** On the wire right now — a second tap must not send it again. */
  readonly #inFlight = new Set<string>();
  /**
   * Writes known to have reached the server, so nothing can put them back.
   *
   * A `load()` already in flight when a retry lands would otherwise re-insert the row it
   * read from the disk a moment earlier, and the panel would warn about work the server has
   * already accepted.
   */
  readonly #landed = new Set<string>();
  #snapshot: FailedWriteView[] = [];
  readonly #listeners = new Set<() => void>();
  readonly #discarded = new Set<(row: FailedWrite) => void>();
  #loading: Promise<void> | null = null;
  #account: AccountRef | null = null;

  constructor(opts: {
    store: FailedWriteStore;
    send?: (path: string, variables: Record<string, unknown>) => Promise<unknown>;
    /** Called when a retry has actually landed, so the server's own lists can catch up. */
    onDelivered?: () => void;
  }) {
    this.#store = opts.store;
    this.#send = opts.send ?? defaultSend;
    this.#onDelivered = opts.onDelivered ?? (() => {});
  }

  /**
   * Tell the log whose session it is running under. It only decides what is SHOWN.
   *
   * The store is per-ORIGIN, so a shop tablet two techs sign into shares it — another
   * person's refused payment is still on the device and still safe, but it is not this
   * person's to see or to clear.
   *
   * There is no adoption any more (MAP 225b). A refusal arrives already owned: it is handed
   * over from its pending row, whose account was stamped when the write was made, so nothing
   * is recorded with nobody signed in and nothing needs claiming by whoever signs in next.
   */
  setAccount(account: AccountRef | null): void {
    const changed = !sameAccountOrNobody(account, this.#account);
    this.#account = account ? { userId: account.userId, orgId: account.orgId } : null;
    if (changed) this.#publish();
  }

  /** Whether this row belongs to the session now signed in. */
  #mine = (row: FailedWrite): boolean => sameAccount(this.#account, accountOf(row));

  /** Read the durable store into memory. Idempotent — the first call does the work. */
  load(): Promise<void> {
    this.#loading ??= this.#store.all().then(
      (rows) => {
        for (const row of rows) {
          // Landed while this read was in the air — putting it back would warn about work
          // the server has already taken.
          if (this.#landed.has(row.mutationId)) continue;
          this.#rows.set(row.mutationId, row);
        }
        this.#publish();
      },
      (err) => {
        // Memoising the REJECTION would leave the log empty and silent for the rest of the
        // session, with refused work behind it. Same reasoning as the upload queue.
        this.#loading = null;
        throw err;
      },
    );
    return this.#loading;
  }

  /**
   * Read the disk again. After the persister's restore, the one-time move of old writes may
   * have given an earlier release's rows with no account one (legacy-writes.ts). Every write
   * here lands on the disk before it reaches memory, so the disk is never the older copy; a
   * landed row stays out.
   */
  reload(): Promise<void> {
    if (this.#loading === null) return this.load();
    return this.#loading.then(
      () => {
        this.#loading = null;
        return this.load();
      },
      () => {
        this.#loading = null;
        return this.load();
      },
    );
  }

  /** Oldest first — the order a tech should work through them. */
  snapshot(): FailedWriteView[] {
    return this.#snapshot;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /**
   * Is a refused write of `path` whose `field` is `value` sitting here, any account's? The photo
   * queue holds a flag's photos while the flag waits on a person here (ticket 03): retried
   * from the panel it lands, and they follow; sent now, they would only be told "not yet".
   * Not read yet is "no": the server's retryable answer covers that moment.
   */
  holds(path: string, field: string, value: string): boolean {
    for (const row of this.#rows.values()) {
      if (row.path === path && row.variables[field] === value) return true;
    }
    return false;
  }

  /** How many of the signed-in tech's writes are sitting here refused. */
  count(): number {
    return this.#snapshot.length;
  }

  /**
   * How many of this account's writes are sitting here refused, read fresh from the disk
   * (another tab's too): the sign-out warning's "needs you". Best effort.
   */
  async countFor(account: AccountRef): Promise<{ waiting: number; refused: number }> {
    const rows = await this.#store.all();
    return {
      waiting: 0,
      refused: rows.filter((row) => sameAccount(account, accountOf(row))).length,
    };
  }

  /**
   * Write down a refusal.
   *
   * Durable first — the caller is about to tell a tech their work is "still here", and it
   * must not say so before it is true.
   *
   * 🔴 **When the disk refuses, the row is kept in memory anyway and says so.** The first
   * version let `put` reject and told the caller; the caller is a cache subscription that
   * could only `void` it, so a full phone meant a refused payment vanished with no trace —
   * this ticket's own defect, reintroduced at the one place it matters most. Holding it for
   * the session is strictly better than silence, and `durable: false` is what stops the
   * panel promising it will still be there tomorrow. Rethrown as well, so a caller that CAN
   * do something still hears about it.
   */
  async record(input: {
    path: string;
    variables: unknown;
    error: unknown;
    /**
     * Whose write it is: the account on its pending row, never whoever is signed in now.
     * Required, so a row can never again be recorded with nobody's name on it.
     */
    account: AccountRef;
    /**
     * An identity to fall back on when the write carries no idempotency key of its own.
     * TanStack's per-session counter is the only thing available; it is not stable across a
     * restart, which is the price of not dropping the row.
     */
    fallbackKey?: string;
  }): Promise<void> {
    const own = keyOf(input.variables);
    /**
     * 🔴 **A write with no idempotency key is TOLD, never dropped.**
     *
     * The first version returned here, reasoning that `idem()` is spread into every
     * replayable call site so the case could not arise. It can: `spine.ts` declares
     * `clientId`/`mutationId` as `.optional()`, so the server accepts a write without them
     * and nothing on the client enforces it. Returning meant a refused payment vanished with
     * no trace — this ticket's defect, hiding behind an assumption.
     *
     * It is recorded and shown; it is never RETRYABLE, because replaying a write the server
     * cannot dedupe is how one payment becomes two. The tech is told to write it down, which
     * is the honest ceiling for a write that cannot safely be sent again.
     */
    const key = own ?? {
      clientId: '',
      mutationId: input.fallbackKey ?? `local:${SESSION}:${input.path}:${Date.now()}`,
    };
    // Already dealt with — delivered, or cleared by a person. The errored mutation lives on
    // in TanStack's cache and keeps emitting; without this it re-creates the row each time.
    if (this.#landed.has(key.mutationId)) return;
    // A boot load that failed left `#rows` empty, hiding every row already on the disk.
    // `load()` un-memoises its own rejection, so this re-arms it at no cost when it worked.
    void this.load().catch(() => {});
    const row: FailedWrite = {
      ...key,
      path: input.path,
      variables: input.variables as Record<string, unknown>,
      userId: input.account.userId,
      orgId: input.account.orgId,
      failedAt: Date.now(),
      reason: refusalReason(input.error),
      // No key of its own ⇒ never retryable: the server cannot dedupe it, so a second send
      // is a second payment.
      retryable: own !== null && isRetryableMutationRefusal(input.error),
      durable: true,
      adopted: false,
    };
    try {
      await this.#store.put(row);
    } catch (err) {
      this.#rows.set(row.mutationId, { ...row, durable: false });
      this.#publish();
      throw err;
    }
    this.#rows.set(row.mutationId, row);
    this.#publish();
  }

  /**
   * Ask again for a write that could still go through.
   *
   * ⚠️ Replays the **original variables**, idempotency key and all. A fresh key would be a
   * second payment — the one thing the server's dedupe cannot absorb, and the exact failure
   * `idem()` exists to prevent. If the write secretly landed the first time and only the
   * response was lost, this applies exactly once.
   *
   * Refuses when offline rather than queueing: under `offlineFirst` the retry would pause
   * into the mutation queue while this row stayed here, and the write would have two lives.
   */
  async retry(mutationId: string): Promise<void> {
    const row = this.#rows.get(mutationId);
    if (!row?.retryable) return;
    // Only this account's: the panel shows nothing else, and this says so where it is enforced.
    if (!this.#mine(row)) return;
    if (this.#inFlight.has(mutationId)) return;
    if (!onlineManager.isOnline()) return;

    this.#inFlight.add(mutationId);
    this.#publish();
    try {
      await this.#send(row.path, row.variables);
    } catch (err) {
      // Kept, not refused (signed out, or another account now): its pending row holds it and
      // `landed()` clears this one when it goes through. What the server said last stands.
      if (isKeptForAccount(err)) return;
      // Keep it, with what the server said THIS time — a 500 that became a 409 has stopped
      // being worth another tap, and the tech needs to be told that rather than offered the
      // button again.
      const updated: FailedWrite = {
        ...row,
        failedAt: Date.now(),
        reason: refusalReason(err),
        retryable: isRetryableMutationRefusal(err),
      };
      await this.#store.put(updated);
      this.#rows.set(mutationId, updated);
      return;
    } finally {
      this.#inFlight.delete(mutationId);
      this.#publish();
    }
    await this.#store.remove(mutationId);
    this.#rows.delete(mutationId);
    this.#publish();
    this.#onDelivered();
  }

  /**
   * Let one go, because a person said so.
   *
   * The only way a row leaves other than the server taking it. Deliberately not called
   * "dismiss": what a tech is doing is deciding this work is dealt with some other way, and
   * the screen says so in those words.
   */
  async discard(mutationId: string): Promise<void> {
    const row = this.#rows.get(mutationId);
    if (!row) return;
    // Fenced for the same reason as `landed`: a twin of this write may still be queued (a
    // retry whose signal died, now a pending row), and its refusal must not write the row
    // straight back after the tech has decided what to do about it.
    this.#landed.add(mutationId);
    // Durable first, the same way round as everything else here: taking it off the screen
    // before the phone has forgotten it means the row comes back at the next reload.
    await this.#store.remove(mutationId);
    this.#rows.delete(mutationId);
    // Told BEFORE the change is published: what was held by this row must be taken over by
    // its listener first, or a reader of the log (the photo queue's drain) sees it gone and acts.
    for (const listener of [...this.#discarded]) listener(row);
    this.#publish();
  }

  /**
   * Hear when a person lets a refused write go (`discard`): what was waiting on it can stop
   * waiting. "Flag a problem" gives a dismissed flag's photos back to its draft
   * (`lib/ticket-photos.ts`).
   */
  onDiscard(listener: (row: FailedWrite) => void): () => void {
    this.#discarded.add(listener);
    return () => void this.#discarded.delete(listener);
  }

  /**
   * Forget a row because the write it describes has landed — by whatever route.
   *
   * 🔴 **The hole this closes is one `retry()` opens itself.** `retry()` refuses to send with
   * no signal, but the signal can die *during* the send: `offlineFirst` then PAUSES the
   * mutation it built, its pending row keeps it as an ordinary queued write (MAP 225b), and
   * the promise `retry()` is awaiting never settles. The write now has two lives — one in the
   * mutation queue, which will deliver it, and one here, which would say forever that it
   * needs a person. A tech would "deal with" a payment that had already gone through.
   *
   * So the log is cleared by the fact of DELIVERY rather than by the path that achieved it,
   * which also covers a queued write that fails once, is logged, and succeeds on a later
   * replay. Silent by design: nothing was refused, so there is nothing to say.
   *
   * ⚠️ **The store is cleared whether or not the row is in memory yet.** On a restart a
   * persisted retry can succeed *before* `load()` has finished reading the disk; an early
   * return would ignore that success, and `load()` would then publish the stale failure row
   * and warn about work the server has already accepted. The row is also remembered as
   * landed so a `load()` still in flight cannot put it back.
   */
  async landed(mutationId: string): Promise<void> {
    // Fence anything a read could put back: a row here, or any write once the disk has been
    // (or is being) read, since a read or a reload may be in the air with the row in it.
    if (this.#rows.has(mutationId) || this.#loading !== null) {
      this.#landed.add(mutationId);
    }
    this.#inFlight.delete(mutationId);
    await this.#store.remove(mutationId);
    this.#rows.delete(mutationId);
    this.#publish();
  }

  /** Rebuild the snapshot once per change, so `getSnapshot` can be identity-stable. */
  #publish(): void {
    this.#snapshot = [...this.#rows.values()]
      .filter(this.#mine)
      .sort(byFailedAt)
      .map((row) => ({ ...row, sending: this.#inFlight.has(row.mutationId) }));
    for (const listener of this.#listeners) listener();
  }
}

/**
 * Re-fire a write through the client's registered defaults.
 *
 * The same machinery a restored mutation runs on: `build()` merges
 * `setMutationDefaults([path])`, which `registerReplay` populated for every replayable path,
 * so a retried write is indistinguishable from the original — including
 * `orderVisitCompletion`'s barrier on `visit.complete`. And `execute()` passes the cache's
 * `onMutate` (a durable, account-stamped pending row first) and the write guard link, so a retry
 * is held to the same rules as the first try (MAP 225b).
 */
function defaultSend(path: string, variables: Record<string, unknown>): Promise<unknown> {
  return queryClient
    .getMutationCache()
    .build(queryClient, { mutationKey: [path.split('.')] })
    .execute(variables);
}

// ── The real store ───────────────────────────────────────────────────────────

let idb: UseStore | undefined;
/** Opened lazily: this module is imported by tests that run in Node, where there is none. */
const store = (): UseStore => (idb ??= createStore('servwell-failed-writes', 'writes'));

/**
 * Its own database, not the query cache's and not the photo queue's. A refused payment must
 * not be evicted by cache housekeeping, and the cache must not be rewritten because one
 * arrived.
 */
export const indexedDbFailedWriteStore: FailedWriteStore = {
  all: () => values<FailedWrite>(store()),
  put: (item) => set(item.mutationId, item, store()),
  remove: (mutationId) => del(mutationId, store()),
};

export const failedWriteLog = new FailedWriteLog({
  store: indexedDbFailedWriteStore,
  // A write that just landed is in no cached list yet, and the screen it belongs to is very
  // likely the one on screen.
  onDelivered: () => void queryClient.invalidateQueries(),
});

/**
 * Read the disk at boot, and keep trying if it will not be read.
 *
 * 🔴 **A single attempt was a silent, session-long blackout.** If the boot read rejects — a
 * locked database, an origin still opening its stores — every refusal already on the disk is
 * invisible until the app is closed and reopened, and only a NEW refusal would have re-armed
 * it. So the same three signals the upload queue uses drive this too: coming back online, and
 * the tab becoming visible, are both moments the storage layer is likely to be healthy again.
 * `load()` memoises success, so the repeats cost nothing once it has worked.
 */
export function startFailedWriteLog(log: FailedWriteLog = failedWriteLog): () => void {
  const attempt = () => void log.load().catch(() => {});
  attempt();
  return onSignalOrVisible(attempt);
}

// ── Reading it from React ────────────────────────────────────────────────────

export function useFailedWriteList(
  log: FailedWriteLog = failedWriteLog,
): FailedWriteView[] {
  const getSnapshot = useCallback(() => log.snapshot(), [log]);
  return useSyncExternalStore(log.subscribe, getSnapshot, getSnapshot);
}

/**
 * How much refused work is sitting on this phone.
 *
 * Read by the banner alongside the mutation cache's own count, and it is the half that
 * survives: `useFailedWrites()` reads the cache, which forgets in about five minutes, so
 * without this the banner goes back to announcing that everything went through over a
 * payment it was told it could not have.
 */
export function useFailedWriteCount(log: FailedWriteLog = failedWriteLog): number {
  return useFailedWriteList(log).length;
}

/**
 * Is there a REFUSED write of this kind, for this thing, still waiting on a person?
 *
 * 🔴 **Without this, the panel makes a double payment more likely rather than less.**
 * `useUnsentWrite` releases a control the moment its write leaves `queued`/`sending` — and
 * `error` is one of the states it leaves to. So a payment that reached the server and lost
 * only its response ends up logged here *and* with its button live again; the next tap calls
 * `idem()`, mints a **fresh** key, and the server's dedupe cannot connect the two. That is
 * 016's defect exactly, one state along, and this panel is what draws the tech back to the
 * button to commit it.
 *
 * So a control that can be fired with no signal asks two questions, not one: is this write
 * still on its way, and is there one sitting here refused. The refused one is retried from
 * the panel, with the ORIGINAL key, which is the only safe way to send it again.
 */
export function useFailedWriteFor(
  path: string,
  id: string | undefined,
  field: string,
  log: FailedWriteLog = failedWriteLog,
): boolean {
  const rows = useFailedWriteList(log);
  if (id === undefined) return false;
  return rows.some((row) => row.path === path && row.variables[field] === id);
}

/** The row's account, or nobody for an earlier release's row recorded with none. */
function accountOf(row: Pick<FailedWrite, 'userId' | 'orgId'>): AccountRef | null {
  return row.userId !== null && row.orgId !== null
    ? { userId: row.userId, orgId: row.orgId }
    : null;
}
